<template>
  <div
    class="app-dialer-call-card-info"
    :class="states"
  >
    <div
      class="icon-button"
      :class="{ [`is-${size}`]: true }"
      data-action="toggle-members"
      @click="$emit('toggle-members')"
    >
      <or-icon :icon="icon" />
    </div>

    <app-dialer-call-card-avatars
      :avatar="avatar"
      :agent="agent"
      :contact="contact"
      :members="members"
      :size="size"
    />

    <div
      class="info"
      :class="{ 'is-clickable': showInfoButton }"
      @mouseenter="isMouseOverInfo = true"
      @mouseleave="isMouseOverInfo = false"
      @click="showInfoButton && $emit('toggle-info')"
    >
      <div
        v-if="title || showInfoButton"
        class="title"
      >
        <span class="text">
          {{ title }}
        </span>

        <or-icon
          v-if="showInfoButton && !description"
          class="info-button"
          icon="info"
          :variant="infoIcon"
        />
      </div>

      <div
        v-if="description"
        class="description"
      >
        <span class="text">
          {{ description }}
        </span>

        <or-icon
          v-if="showInfoButton"
          class="info-button"
          icon="info"
          :variant="infoIcon"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { OrIconV3 as OrIcon } from '@onereach/ui-components-25';
import { TranslateResult } from 'vue-i18n';

import AppDialerCallCardAvatars from './AppDialerCallCardAvatars.vue';
import { ConferenceMember } from '../../types';
import { getContactName } from '@/common';
import {
  AVATAR_PROP,
  AGENT_PROP,
  CONTACT_PROP,
  MEMBERS_PROP,
  SIZE_PROP,
} from '../../constants';

export default defineComponent({
  name: 'AppDialerCallCardInfo',
  components: {
    OrIcon,
    AppDialerCallCardAvatars,
  },
  props: {
    avatar: AVATAR_PROP,
    agent: AGENT_PROP,
    contact: CONTACT_PROP,
    identifier: {
      type: String,
      default: '',
    },
    members: MEMBERS_PROP,
    size: SIZE_PROP,
    isExpanded: Boolean,
    /** An alternative layout for limited horizontal space */
    isGridLayout: Boolean,
    showInfoButton: Boolean,
    isInfoButtonActive: Boolean,
  },
  emits: ['toggle-members', 'toggle-info'],
  data() {
    return {
      isMouseOverInfo: false,
    };
  },
  computed: {
    icon(): string {
      if (this.size === 'mobile') {
        return 'unfold_more';
      }

      return this.isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right';
    },

    infoIcon(): string {
      return this.isMouseOverInfo || this.isInfoButtonActive
        ? 'filled'
        : 'outlined';
    },

    states(): object {
      return {
        [`is-${this.size}`]: true,
        'is-grid-layout': this.isGridLayout,
      };
    },

    /** Contact Name -> Phone Number -> N Call Members */
    title(): TranslateResult {
      if (this.contact) {
        return getContactName(this.contact, this.$t.bind(this));
      }

      if (this.identifier) {
        return this.identifier;
      }

      const data = { count: this.members.length };
      return this.$t('hitl.modules.dialer.call-members-title', data);
    },

    /**
     * [+Contact] Identifier -> No Description / N Call Members
     * [-Contact] Placeholder Label
     */
    description(): TranslateResult {
      if (this.contact && this.identifier) {
        return this.identifier;
      }

      if (this.contact && !this.additional.length) {
        return '';
      }

      if (this.contact && this.additional.length) {
        const data = { count: this.members.length };
        return this.$t('hitl.modules.dialer.call-members-title', data);
      }

      return this.$t('hitl.modules.dialer.call-members-description', {
        name: this.$t('hitl.modules.dialer.no-contact-label'),
      });
    },

    /** Members without agent or contact */
    additional(): ConferenceMember[] {
      return this.members.filter(i => {
        const isAgent = i.memberId === this.agent.id;
        const isContact = i.memberId === this.contact?.contactId;
        return !isAgent && !isContact;
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;
@import '../../styles';

.app-dialer-call-card-info {
  @include app-dialer-icon-button-inverse;
  --card-title-font-size: var(--fs-1);
  --card-description-font-size: var(--fs-0);
  --card-avatars-left-margin: 0.375rem;
  --card-info-left-margin: 0.5rem;
  --card-info-button-font-size: 1rem;

  display: inline-grid;
  grid-template-columns: auto auto auto;
  grid-template-rows: auto;
  align-items: center;
  width: fit-content;
  color: var(--or-c-inverse-on-surface);

  &.is-mobile {
    --card-title-font-size: var(--fs-2);
    --card-description-font-size: var(--fs-1);
    --card-info-button-font-size: 1.25rem;
  }

  &.is-grid-layout {
    grid-template-columns: auto auto;
    grid-template-rows: auto auto;
    row-gap: 0.5rem;

    .info {
      grid-column: 2;
    }
  }

  .app-dialer-call-card-avatars {
    margin-left: var(--card-avatars-left-margin);
  }

  .info {
    display: flex;
    flex-direction: column;
    margin-left: var(--card-info-left-margin);
    overflow: hidden;

    &.is-clickable {
      cursor: pointer;
    }

    .text {
      @include text-overflow;
    }

    .title,
    .description {
      display: flex;
      align-items: center;
    }

    .title {
      font-size: var(--card-title-font-size);
      font-weight: var(--fw-1);
      line-height: 1.5;
    }

    .description {
      font-size: var(--card-description-font-size);
      line-height: 1.5;
    }

    .info-button {
      margin-left: 0.5rem;

      ::v-deep .or-icon-v3 {
        font-size: var(--card-info-button-font-size);
      }
    }
  }
}
</style>
